import {
  type LinkProps,
  Outlet,
  createFileRoute,
  useNavigate,
} from '@tanstack/react-router';
import { type PropsWithChildren, useEffect } from 'react';

import ListPageHeader from '@/components/ListPageHeader';
import { ProductLogoMediumMarketplace as LogoMarketplace } from '@/components/Logo';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { useStoreHeader } from './-query';

export const Route = createFileRoute('/_staff/marketplace/_store')({
  component: MarketplaceHeader,
});

function MarketplaceHeader() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.marketplace',
  });
  const { t: tv } = useTranslation(defaultNS, {
    keyPrefix: 'pages.vendor.marketplace',
  });
  const navigate = useNavigate();
  const { data: storeHeader } = useStoreHeader();

  // ストアが利用可能でない場合はリダイレクト
  useEffect(() => {
    if (storeHeader && !storeHeader.isAvailable) {
      navigate({ to: '/marketplace/unavailable' });
    }
  }, [storeHeader, navigate]);

  if (!storeHeader) {
    return null;
  }

  // Customer・Supplierそれぞれ利用可能な画面のタブを表示
  const tabs: PropsWithChildren<LinkProps>[] = [
    ...(storeHeader.isCustomer
      ? [
          {
            to: '/marketplace/store/devices' as const,
            children: t('store'),
          },
          {
            to: '/marketplace/store/licenses' as const,
            children: t('licenseStore'),
          },
          {
            to: '/marketplace/store/sent-orders' as const,
            children: t('orders'),
          },
        ]
      : []),
    ...(storeHeader.isSupplier
      ? [
          {
            /**
             * 「発注(Orders)」と「受注(Sales Orders)」のラベル整理はこちら↓
             * @see https://dress-code-inc.slack.com/archives/C086TDGRAL8/p1739511608533199?thread_ts=1739491203.075549&cid=C086TDGRAL8
             * @see https://youneedaken.hatenablog.com/entry/2022/12/26/131026
             */
            to: '/marketplace/store/sales-orders' as const,
            children: tv('salesOrders'),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col">
      <ListPageHeader
        icon={<LogoMarketplace />}
        title={t('marketplace')}
        tabs={tabs}
      />
      <Outlet />
    </div>
  );
}
